import React, { useMemo, useState, useEffect } from 'react'
import { useTable, useGlobalFilter, useFilters, useSortBy } from 'react-table'
import { FaSearch } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { obtenerUsuarios } from '../../api'

function FilterSection({ title, options, selected, onSelect }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">{title}</h3>
      <ul className="space-y-1">
        {options.map(([label, value]) => (
          <li key={label}>
            <button
              className={`text-sm ${selected === value ? 'text-primary' : 'text-[rgb(var(--color-text))]'}`}
              onClick={() => onSelect(value)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Toolbar({ title }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-[rgb(var(--color-text))]">{title}</h2>
    </div>
  )
}

const Users = () => {
  const [rawData, setRawData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasOrdered, setHasOrdered] = useState(null)
  const [timeFilter, setTimeFilter] = useState('all')
  const navigate = useNavigate()

  useEffect(() => {
    obtenerUsuarios()
      .then(data => {
        setRawData(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error al obtener usuarios:', err)
        if (err.response?.status === 401) {
          navigate('/admin/auth')
        }
        setError(err.message)
        setLoading(false)
      })
  }, [navigate])

  const columns = useMemo(
    () => [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Orders', accessor: 'orders' },
      { Header: 'Total spent', accessor: 'totalSpent' },
      { Header: 'Last seen', accessor: 'lastSeen' }
    ],
    []
  )

  const data = useMemo(() => {
    const list = Array.isArray(rawData) ? rawData : []
    return list.filter(row => {
      if (hasOrdered === true && row.orders === 0) return false
      if (hasOrdered === false && row.orders > 0) return false
      if (timeFilter !== 'all') {
        const mapDays = { today: 0, week: 7, month: 30 }[timeFilter]
        const diff =
          (new Date() - new Date(row.lastSeen.split('/').reverse().join('-'))) /
          (1000 * 60 * 60 * 24)
        if (diff > mapDays) return false
      }
      return true
    })
  }, [rawData, hasOrdered, timeFilter])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter
  } = useTable({ columns, data }, useFilters, useGlobalFilter, useSortBy)

  if (loading) return <div className="p-6 text-white">Loading users…</div>
  if (error) return <div className="p-6 text-red-400">{error}</div>

  return (
    <div className="flex h-full">
      <aside className="w-64 p-4 bg-[rgb(var(--color-bg))] border rounded-lg text-[rgb(var(--color-text))] space-y-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-2" />
          <input
            type="text"
            placeholder="Search…"
            className="pl-10 pr-3 py-2 w-full rounded bg-hover-bg text-primary"
            value={state.globalFilter || ''}
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
        <FilterSection
          title="HAS ORDERED"
          options={[['Yes', true], ['No', false], ['All', null]]}
          selected={hasOrdered}
          onSelect={setHasOrdered}
        />
        <FilterSection
          title="LAST VISITED"
          options={[['Today', 'today'], ['This week', 'week'], ['Last month', 'month'], ['All', 'all']]}
          selected={timeFilter}
          onSelect={setTimeFilter}
        />
      </aside>

      <main className="flex-1 overflow-auto p-6 bg-[rgb(var(--color-bg))]">
        <Toolbar title="Users" />
        <div className="border rounded-lg overflow-hidden">
          <table {...getTableProps()} className="w-full table-auto text-left">
            <thead className="bg-hover-bg text-[rgb(var(--color-text))]  border">
              {headerGroups.map(hg => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map(col => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      className="px-4 py-2 uppercase text-sm font-medium  "
                    >
                      {col.render('Header')}
                      <span>{col.isSorted ? (col.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <tr
                    {...row.getRowProps()}
                    className=" hover:bg-hover-bg cursor-pointer"
                    onDoubleClick={() => navigate(`/admin/users/${row.original.id}`)}

                  >
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()} className="px-4 py-3 text-[rgb(var(--color-text))] border-b">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}

export default Users
